/**
 * Hero trust-line live horse count — display formatting + the best-effort
 * client fetch.
 *
 * The count comes from tph-api's PUBLIC /api/v1/public/stats/horse-count via
 * the marketplace's own same-origin /api/horse-count pass-through
 * (server/api/horse-count.js — same proxy pattern as the search-telemetry
 * calls in src/util/searchTelemetry.js). Strictly best-effort: any failure
 * resolves to null and the hero keeps the static HORSE_COUNT fallback.
 */

import { useEffect, useState } from 'react';

import { apiBaseUrl } from '../../util/api';
import { HORSE_COUNT } from './homeContent';

// Hard ceiling on how long the hero waits for the live number. The fetch runs
// after mount and never blocks render; past this the static fallback stays.
export const HORSE_COUNT_TIMEOUT_MS = 2000;

/**
 * Format a live horse count for the hero trust line. At 100 and above: rounded
 * DOWN to the nearest hundred, locale thousands separators, trailing plus
 * (5447 → '5,400+'). Below 100 the exact integer shows with NO plus (47 → '47')
 * — flooring small counts to '0+' reads as having no horses at all.
 *
 * Returns the static HORSE_COUNT fallback ONLY when the value is
 * null/undefined/non-numeric (fetch failed or not yet resolved).
 *
 * @param {*} count the live count (expected integer) or null/undefined
 * @returns {string} display string, e.g. '5,400+' or '47'
 */
export const formatHorseCount = count => {
  if (typeof count !== 'number' || !Number.isFinite(count)) {
    return HORSE_COUNT;
  }
  if (count < 100) {
    return `${Math.floor(count)}`;
  }
  const floored = Math.floor(count / 100) * 100;
  return `${floored.toLocaleString('en-US')}+`;
};

/**
 * Fetch the live count. Resolves to the numeric count, or null on ANY failure
 * (timeout, non-200, malformed body) — never throws, never spins.
 *
 * @returns {Promise<number|null>}
 */
/**
 * Live trust-line count hook — shared by the homepage hero and the Crown
 * auth shell trust lines. Fetches AFTER mount (never in any loadData / SSR
 * path, so it cannot delay first paint); consumers render immediately with
 * the static fallback and the number updates in place when the live value
 * resolves. Any failure resolves to null → fallback simply remains.
 *
 * @returns {string} formatted display count, e.g. '5,400+' (or the fallback)
 */
export const useLiveHorseCount = () => {
  const [liveCount, setLiveCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchHorseCount().then(count => {
      if (!cancelled && count !== null) {
        setLiveCount(count);
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // formatHorseCount returns the static HORSE_COUNT fallback while liveCount
  // is null (pending or failed).
  return formatHorseCount(liveCount);
};

export const fetchHorseCount = async () => {
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') {
    return null;
  }
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), HORSE_COUNT_TIMEOUT_MS);
  try {
    const response = await window.fetch(`${apiBaseUrl()}/api/horse-count`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
    if (!response.ok) {
      return null;
    }
    const body = await response.json();
    const count = body && body.count;
    return typeof count === 'number' && Number.isFinite(count) ? count : null;
  } catch (e) {
    return null; // abort, network error, or invalid JSON — fall back silently
  } finally {
    clearTimeout(timer);
  }
};
